import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutations and queries

// shopIds: [Int!]!
// name: String!
// expiresAt: String!
// amount: Float!
// type: PromoCodeTypeEnum!
// minDeposit: Float. promo code body
export const CREATE_PROMO_MUTATION = gql`
  mutation CreatePromo($input: CreatePromoInput!) {
    createPromo(input: $input) {
      promoCodes {
        id
        type
        code
        name
        amount
        minDeposit
        expiresAt
        isActive
        createdAt
        ownerId
        owner {
          id
          username
          name
        }
      }
    }
  }
`;

export const GET_PROMO_CODES_QUERY = gql`
  query GetPromoCodes($environmentId: Int, $ownerId: Int) {
    promoCodes(environmentId: $environmentId, ownerId: $ownerId) {
      id
      code
      name
      amount
      type
      ownerId
      expiresAt
      createdAt
      minDeposit
      isActive
      owner {
        id
        username
        name
        environment {
          id
          host
        }
      }
    }
  }
`;

export const UPDATE_PROMO_MUTATION = gql`
  mutation UpdatePromo($input: UpdatePromoInput!) {
    updatePromo(input: $input) {
      id
      code
      name
      amount
      type
      isActive
      expiresAt
      ownerId
      minDeposit
    }
  }
`;

export const REMOVE_PROMO_MUTATION = gql`
  mutation RemovePromo($id: Int!) {
    removePromo(id: $id)
  }
`;

// Types
export enum PromoType {
  REGISTRATION = 'REGISTRATION',
  DEPOSIT = 'DEPOSIT',
}

export interface PromoCode {
  id: number;
  code: string;
  name: string;
  amount: number;
  type: PromoType;
  ownerId: number;
  expiresAt: string;
  createdAt: string;
  minDeposit?: number;
  isActive?: boolean;
  owner?: {
    id: number;
    username: string;
    name?: string;
    environment?: {
      host: string;
    };
  };
}

export interface CreatePromoInput {
  shopIds: number[];
  name: string;
  expiresAt: string;
  amount: number;
  type: PromoType;
  minDeposit?: number;
}

export interface UpdatePromoInput {
  id: number;
  isActive?: boolean;
  expiresAt?: string;
  amount?: number;
  minDeposit?: number;
}

export interface PromoCodeWhereInput {
  environmentId?: number;
  ownerId?: number;
}

// Service class
class PromoService {
  async createPromo(input: CreatePromoInput): Promise<PromoCode[]> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_PROMO_MUTATION,
        variables: { input },
      });
      return data.createPromo.promoCodes;
    } catch (error) {
      console.error('Error creating promo:', error);
      throw error;
    }
  }

  async getPromoCodes(where?: PromoCodeWhereInput): Promise<PromoCode[]> {
    try {
      const { data } = await apolloClient.query({
        query: GET_PROMO_CODES_QUERY,
        variables: {
          environmentId: where?.environmentId,
          ownerId: where?.ownerId,
        },
        fetchPolicy: 'network-only',
      });
      return data.promoCodes;
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      throw error;
    }
  }

  async updatePromo(input: UpdatePromoInput): Promise<PromoCode> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_PROMO_MUTATION,
        variables: { input },
      });
      return data.updatePromo;
    } catch (error) {
      console.error('Error updating promo:', error);
      throw error;
    }
  }

  async deletePromoCode(id: number): Promise<boolean> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: REMOVE_PROMO_MUTATION,
        variables: { id },
      });
      return data.removePromo;
    } catch (error) {
      console.error('Error deleting promo code:', error);
      throw error;
    }
  }
}

export const promoService = new PromoService();
